import { z } from 'zod';
import {
  BLUSH_COLORS,
  EYE_COLORS,
  EYESHADOW_COLORS,
  HAIR_COLORS,
  HEX_COLOR_RE,
  LIP_COLORS,
  PALETTE,
  SKIN_TONES,
  colorName,
} from './colors';

// ─────────────────────────────────────────────────────────────────────────────
// Ids
// ─────────────────────────────────────────────────────────────────────────────

export const BODY_TYPES = ['a', 'b', 'c', 'd'] as const;
export type BodyType = (typeof BODY_TYPES)[number];
export const BODY_TYPE_INFO: Record<BodyType, { label: string; blurb: string }> = {
  a: { label: 'Slim', blurb: 'Slim, narrow shoulders' },
  b: { label: 'Curvy', blurb: 'Curvy, defined waist' },
  c: { label: 'Athletic', blurb: 'Athletic, broad shoulders' },
  d: { label: 'Neutral', blurb: 'Neutral, androgynous' },
};

export const HAIR_STYLES = [
  'bald',
  'buzz',
  'short-crop',
  'pixie',
  'bob',
  'long-straight',
  'long-wavy',
  'ponytail',
  'pigtails',
  'bun',
  'space-buns',
  'braids',
  'afro',
  'curly',
  'mohawk',
  'side-part',
] as const;
export type HairStyleId = (typeof HAIR_STYLES)[number];

export const TOP_IDS = [
  'tee',
  'tank',
  'blouse',
  'hoodie',
  'crop',
  'jacket',
  'sweater',
  'shirt',
  'puffer',
  'vest',
  'bodysuit',
  'cardigan',
] as const;
export type TopId = (typeof TOP_IDS)[number];

export const BOTTOM_IDS = [
  'jeans',
  'shorts',
  'skirt-mini',
  'skirt-midi',
  'skirt-long',
  'leggings',
  'cargo',
  'wide-leg',
  'joggers',
  'culottes',
] as const;
export type BottomId = (typeof BOTTOM_IDS)[number];

export const DRESS_IDS = [
  'a-line',
  'gown',
  'slip',
  'ballgown',
  'wrap',
  'jumpsuit',
  'tutu',
  'shift',
  'mermaid',
  'sundress',
] as const;
export type DressId = (typeof DRESS_IDS)[number];

export const SHOE_IDS = [
  'sneakers',
  'heels',
  'boots',
  'sandals',
  'platforms',
  'flats',
  'combat',
  'loafers',
  'mary-janes',
  'slides',
] as const;
export type ShoeId = (typeof SHOE_IDS)[number];

export const ACCESSORY_IDS = [
  'glasses',
  'sunglasses',
  'hat-cap',
  'hat-beret',
  'hat-wide',
  'crown',
  'tiara',
  'earrings',
  'necklace',
  'choker',
  'scarf',
  'bag',
  'backpack',
  'watch',
  'bracelet',
  'belt',
  'bow',
  'headband',
  'wings',
  'cape',
] as const;
export type AccessoryId = (typeof ACCESSORY_IDS)[number];

export const PATTERN_IDS = [
  'solid',
  'stripes',
  'polka',
  'plaid',
  'sequins',
  'denim',
  'floral',
  'checker',
  'hearts',
  'stars',
  'zebra',
  'gradient',
  'glitter',
] as const;
export type PatternId = (typeof PATTERN_IDS)[number];

export const EYE_SHAPES = ['round', 'almond', 'wide'] as const;
export type EyeShape = (typeof EYE_SHAPES)[number];
export const BROW_STYLES = ['thin', 'natural', 'bold'] as const;
export type BrowStyle = (typeof BROW_STYLES)[number];
export const MOUTH_STYLES = ['smile', 'smirk', 'neutral', 'grin'] as const;
export type MouthStyle = (typeof MOUTH_STYLES)[number];

export const HEIGHT_MIN = 0.9;
export const HEIGHT_MAX = 1.1;
/** Maximum accessories worn at once. */
export const MAX_ACCESSORIES = 6;

// ─────────────────────────────────────────────────────────────────────────────
// Zod schemas + types
// ─────────────────────────────────────────────────────────────────────────────

export const HexColorSchema = z.string().regex(HEX_COLOR_RE, 'expected a #rrggbb colour');

export const AvatarConfigSchema = z.object({
  body: z.enum(BODY_TYPES),
  height: z.number().min(HEIGHT_MIN).max(HEIGHT_MAX),
  skin: HexColorSchema,
  hair: z.object({ style: z.enum(HAIR_STYLES), color: HexColorSchema }),
  eyes: z.object({ color: HexColorSchema, shape: z.enum(EYE_SHAPES) }),
  brows: z.enum(BROW_STYLES),
  mouth: z.enum(MOUTH_STYLES),
  makeup: z.object({
    lips: HexColorSchema.nullable(),
    blush: HexColorSchema.nullable(),
    eyeshadow: HexColorSchema.nullable(),
    liner: z.boolean(),
    freckles: z.boolean(),
  }),
});
export type AvatarConfig = z.infer<typeof AvatarConfigSchema>;

export const TopPieceSchema = z.object({
  id: z.enum(TOP_IDS),
  color: HexColorSchema,
  pattern: z.enum(PATTERN_IDS),
  accent: HexColorSchema.optional(),
});
export const BottomPieceSchema = z.object({
  id: z.enum(BOTTOM_IDS),
  color: HexColorSchema,
  pattern: z.enum(PATTERN_IDS),
});
export const DressPieceSchema = z.object({
  id: z.enum(DRESS_IDS),
  color: HexColorSchema,
  pattern: z.enum(PATTERN_IDS),
  accent: HexColorSchema.optional(),
});
export const ShoePieceSchema = z.object({ id: z.enum(SHOE_IDS), color: HexColorSchema });
export const AccessoryPieceSchema = z.object({ id: z.enum(ACCESSORY_IDS), color: HexColorSchema });

export const OutfitConfigSchema = z.object({
  top: TopPieceSchema.nullable(),
  bottom: BottomPieceSchema.nullable(),
  /** When set, overrides top + bottom. */
  dress: DressPieceSchema.nullable(),
  shoes: ShoePieceSchema.nullable(),
  accessories: z.array(AccessoryPieceSchema).max(MAX_ACCESSORIES),
});
export type OutfitConfig = z.infer<typeof OutfitConfigSchema>;
export type TopPiece = z.infer<typeof TopPieceSchema>;
export type BottomPiece = z.infer<typeof BottomPieceSchema>;
export type DressPiece = z.infer<typeof DressPieceSchema>;
export type ShoePiece = z.infer<typeof ShoePieceSchema>;
export type AccessoryPiece = z.infer<typeof AccessoryPieceSchema>;

/**
 * Look = AvatarConfig + OutfitConfig. This is what walks the runway, what the
 * judges see, and what `show_entries.look` stores.
 */
export const LookSchema = z.object({ avatar: AvatarConfigSchema, outfit: OutfitConfigSchema });
export type Look = z.infer<typeof LookSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Catalog
// ─────────────────────────────────────────────────────────────────────────────

export type CatalogSlot = 'hair' | 'top' | 'bottom' | 'dress' | 'shoes' | 'accessory';
export type CatalogId = HairStyleId | TopId | BottomId | DressId | ShoeId | AccessoryId;
export type CatalogItem = { slot: CatalogSlot; id: CatalogId; label: string; tags: string[] };

type Entry = readonly [label: string, tags: readonly string[]];

const HAIR_TABLE: Record<HairStyleId, Entry> = {
  bald: ['Bald', ['minimal', 'bold', 'clean']],
  buzz: ['Buzz cut', ['short', 'edgy', 'sporty']],
  'short-crop': ['Short crop', ['short', 'neat', 'classic']],
  pixie: ['Pixie', ['short', 'chic', 'playful']],
  bob: ['Bob', ['medium', 'chic', 'retro']],
  'long-straight': ['Long straight', ['long', 'sleek', 'classic']],
  'long-wavy': ['Long wavy', ['long', 'romantic', 'beachy']],
  ponytail: ['Ponytail', ['sporty', 'neat', 'high']],
  pigtails: ['Pigtails', ['cute', 'playful', 'youthful']],
  bun: ['Top bun', ['elegant', 'neat', 'formal']],
  'space-buns': ['Space buns', ['playful', 'festival', 'cute']],
  braids: ['Braids', ['long', 'textured', 'elegant']],
  afro: ['Afro', ['voluminous', 'bold', 'natural']],
  curly: ['Curly', ['voluminous', 'bouncy', 'natural']],
  mohawk: ['Mohawk', ['punk', 'edgy', 'bold']],
  'side-part': ['Side part', ['classic', 'polished', 'retro']],
};

const TOP_TABLE: Record<TopId, Entry> = {
  tee: ['T-shirt', ['casual', 'everyday', 'relaxed']],
  tank: ['Tank top', ['casual', 'summer', 'sporty']],
  blouse: ['Blouse', ['dressy', 'soft', 'office']],
  hoodie: ['Hoodie', ['casual', 'street', 'cozy']],
  crop: ['Crop top', ['trendy', 'summer', 'party']],
  jacket: ['Jacket', ['layer', 'street', 'cool']],
  sweater: ['Sweater', ['cozy', 'winter', 'soft']],
  shirt: ['Button-up shirt', ['smart', 'classic', 'office']],
  puffer: ['Puffer jacket', ['winter', 'street', 'bold']],
  vest: ['Vest', ['layer', 'smart', 'retro']],
  bodysuit: ['Bodysuit', ['sleek', 'party', 'dance']],
  cardigan: ['Cardigan', ['cozy', 'soft', 'preppy']],
};

const BOTTOM_TABLE: Record<BottomId, Entry> = {
  jeans: ['Jeans', ['casual', 'denim', 'everyday']],
  shorts: ['Shorts', ['summer', 'casual', 'sporty']],
  'skirt-mini': ['Mini skirt', ['party', 'flirty', 'short']],
  'skirt-midi': ['Midi skirt', ['chic', 'office', 'classic']],
  'skirt-long': ['Long skirt', ['boho', 'elegant', 'flowy']],
  leggings: ['Leggings', ['sporty', 'sleek', 'comfy']],
  cargo: ['Cargo pants', ['street', 'utility', 'relaxed']],
  'wide-leg': ['Wide-leg trousers', ['chic', 'retro', 'flowy']],
  joggers: ['Joggers', ['sporty', 'comfy', 'street']],
  culottes: ['Culottes', ['chic', 'summer', 'relaxed']],
};

const DRESS_TABLE: Record<DressId, Entry> = {
  'a-line': ['A-line dress', ['classic', 'flattering', 'day']],
  gown: ['Evening gown', ['formal', 'glam', 'red-carpet']],
  slip: ['Slip dress', ['sleek', 'minimal', 'evening']],
  ballgown: ['Ballgown', ['formal', 'princess', 'dramatic']],
  wrap: ['Wrap dress', ['classic', 'day', 'flattering']],
  jumpsuit: ['Jumpsuit', ['modern', 'sleek', 'party']],
  tutu: ['Tutu dress', ['playful', 'dance', 'fluffy']],
  shift: ['Shift dress', ['mod', 'retro', 'office']],
  mermaid: ['Mermaid dress', ['glam', 'dramatic', 'formal']],
  sundress: ['Sundress', ['summer', 'breezy', 'day']],
};

const SHOE_TABLE: Record<ShoeId, Entry> = {
  sneakers: ['Sneakers', ['casual', 'sporty', 'street']],
  heels: ['Heels', ['glam', 'formal', 'evening']],
  boots: ['Boots', ['autumn', 'edgy', 'classic']],
  sandals: ['Sandals', ['summer', 'beachy', 'casual']],
  platforms: ['Platforms', ['retro', 'bold', 'party']],
  flats: ['Ballet flats', ['dainty', 'day', 'classic']],
  combat: ['Combat boots', ['punk', 'street', 'edgy']],
  loafers: ['Loafers', ['preppy', 'smart', 'classic']],
  'mary-janes': ['Mary Janes', ['cute', 'retro', 'dainty']],
  slides: ['Slides', ['casual', 'summer', 'comfy']],
};

const ACCESSORY_TABLE: Record<AccessoryId, Entry> = {
  glasses: ['Glasses', ['smart', 'nerdy-chic', 'face']],
  sunglasses: ['Sunglasses', ['cool', 'summer', 'face']],
  'hat-cap': ['Baseball cap', ['sporty', 'street', 'head']],
  'hat-beret': ['Beret', ['parisian', 'artsy', 'head']],
  'hat-wide': ['Wide-brim hat', ['glam', 'summer', 'head']],
  crown: ['Crown', ['royal', 'fantasy', 'head']],
  tiara: ['Tiara', ['princess', 'sparkly', 'head']],
  earrings: ['Earrings', ['sparkly', 'elegant', 'jewelry']],
  necklace: ['Necklace', ['elegant', 'jewelry', 'neck']],
  choker: ['Choker', ['edgy', '90s', 'neck']],
  scarf: ['Scarf', ['cozy', 'chic', 'neck']],
  bag: ['Handbag', ['chic', 'practical', 'hand']],
  backpack: ['Backpack', ['school', 'street', 'practical']],
  watch: ['Watch', ['smart', 'classic', 'wrist']],
  bracelet: ['Bracelet', ['sparkly', 'jewelry', 'wrist']],
  belt: ['Belt', ['waist', 'structure', 'classic']],
  bow: ['Hair bow', ['cute', 'playful', 'head']],
  headband: ['Headband', ['preppy', 'cute', 'head']],
  wings: ['Fairy wings', ['fantasy', 'costume', 'dramatic']],
  cape: ['Cape', ['hero', 'dramatic', 'costume']],
};

function tableToItems<Id extends CatalogId>(slot: CatalogSlot, table: Record<Id, Entry>): CatalogItem[] {
  return (Object.keys(table) as Id[]).map((id) => {
    const [label, tags] = table[id];
    return { slot, id, label, tags: [...tags] };
  });
}

/** Every wearable/hair id with a label and tags, for pickers and judge prompts. */
export const CATALOG: readonly CatalogItem[] = [
  ...tableToItems('hair', HAIR_TABLE),
  ...tableToItems('top', TOP_TABLE),
  ...tableToItems('bottom', BOTTOM_TABLE),
  ...tableToItems('dress', DRESS_TABLE),
  ...tableToItems('shoes', SHOE_TABLE),
  ...tableToItems('accessory', ACCESSORY_TABLE),
];

export function catalogFor(slot: CatalogSlot): CatalogItem[] {
  return CATALOG.filter((c) => c.slot === slot);
}

export function catalogLabel(slot: CatalogSlot, id: string): string {
  return CATALOG.find((c) => c.slot === slot && c.id === id)?.label ?? id;
}

export const PATTERN_LABELS: Record<PatternId, string> = {
  solid: 'Solid',
  stripes: 'Stripes',
  polka: 'Polka dots',
  plaid: 'Plaid',
  sequins: 'Sequins',
  denim: 'Denim',
  floral: 'Floral',
  checker: 'Checkerboard',
  hearts: 'Hearts',
  stars: 'Stars',
  zebra: 'Zebra',
  gradient: 'Gradient',
  glitter: 'Glitter',
};

/** Adjective form used in prose ("a striped royal blue tee"). Empty for solid. */
const PATTERN_ADJECTIVE: Record<PatternId, string> = {
  solid: '',
  stripes: 'striped',
  polka: 'polka-dot',
  plaid: 'plaid',
  sequins: 'sequined',
  denim: 'denim',
  floral: 'floral',
  checker: 'checkered',
  hearts: 'heart-print',
  stars: 'star-print',
  zebra: 'zebra-print',
  gradient: 'ombré',
  glitter: 'glittery',
};

// ─────────────────────────────────────────────────────────────────────────────
// Defaults + randomisation
// ─────────────────────────────────────────────────────────────────────────────

export const DEFAULT_AVATAR: AvatarConfig = {
  body: 'd',
  height: 1,
  skin: '#D19E73',
  hair: { style: 'long-wavy', color: '#4A3226' },
  eyes: { color: '#6B4423', shape: 'almond' },
  brows: 'natural',
  mouth: 'smile',
  makeup: { lips: null, blush: null, eyeshadow: null, liner: false, freckles: false },
};

export const DEFAULT_OUTFIT: OutfitConfig = {
  top: { id: 'tee', color: '#FFFFFF', pattern: 'solid' },
  bottom: { id: 'jeans', color: '#3B5B8C', pattern: 'denim' },
  dress: null,
  shoes: { id: 'sneakers', color: '#FFFFFF' },
  accessories: [],
};

export const DEFAULT_LOOK: Look = { avatar: DEFAULT_AVATAR, outfit: DEFAULT_OUTFIT };

export type Rng = () => number;

/** Deterministic mulberry32 PRNG. Pass a number or any string. */
export function createRng(seed: number | string): Rng {
  let a = typeof seed === 'number' ? seed >>> 0 : hashString(seed);
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function hashString(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

export function pick<T>(items: readonly T[], rng: Rng = Math.random): T {
  const item = items[Math.floor(rng() * items.length)];
  if (item === undefined) throw new Error('pick() from empty list');
  return item;
}

const chance = (p: number, rng: Rng) => rng() < p;

export function randomAvatar(rng: Rng = Math.random): AvatarConfig {
  return {
    body: pick(BODY_TYPES, rng),
    height: Math.round((HEIGHT_MIN + rng() * (HEIGHT_MAX - HEIGHT_MIN)) * 100) / 100,
    skin: pick(SKIN_TONES, rng).hex,
    hair: { style: pick(HAIR_STYLES, rng), color: pick(HAIR_COLORS, rng).hex },
    eyes: { color: pick(EYE_COLORS, rng).hex, shape: pick(EYE_SHAPES, rng) },
    brows: pick(BROW_STYLES, rng),
    mouth: pick(MOUTH_STYLES, rng),
    makeup: {
      lips: chance(0.6, rng) ? pick(LIP_COLORS, rng).hex : null,
      blush: chance(0.5, rng) ? pick(BLUSH_COLORS, rng).hex : null,
      eyeshadow: chance(0.4, rng) ? pick(EYESHADOW_COLORS, rng).hex : null,
      liner: chance(0.4, rng),
      freckles: chance(0.25, rng),
    },
  };
}

export function randomOutfit(rng: Rng = Math.random): OutfitConfig {
  const wearDress = chance(0.4, rng);
  const accessoryCount = Math.floor(rng() * 4); // 0..3
  const accessoryPool = [...ACCESSORY_IDS];
  const accessories: AccessoryPiece[] = [];
  for (let i = 0; i < accessoryCount && accessoryPool.length; i++) {
    const idx = Math.floor(rng() * accessoryPool.length);
    const [id] = accessoryPool.splice(idx, 1);
    if (id) accessories.push({ id, color: pick(PALETTE, rng).hex });
  }
  const patternedPick = (): PatternId => (chance(0.55, rng) ? 'solid' : pick(PATTERN_IDS, rng));
  return {
    top: wearDress ? null : { id: pick(TOP_IDS, rng), color: pick(PALETTE, rng).hex, pattern: patternedPick() },
    bottom: wearDress
      ? null
      : { id: pick(BOTTOM_IDS, rng), color: pick(PALETTE, rng).hex, pattern: patternedPick() },
    dress: wearDress
      ? {
          id: pick(DRESS_IDS, rng),
          color: pick(PALETTE, rng).hex,
          pattern: patternedPick(),
          ...(chance(0.4, rng) ? { accent: pick(PALETTE, rng).hex } : {}),
        }
      : null,
    shoes: chance(0.95, rng) ? { id: pick(SHOE_IDS, rng), color: pick(PALETTE, rng).hex } : null,
    accessories,
  };
}

export function randomLook(rng: Rng = Math.random): Look {
  return { avatar: randomAvatar(rng), outfit: randomOutfit(rng) };
}

/** A dress overrides top+bottom; normalise so renderers never see both. */
export function normalizeOutfit(outfit: OutfitConfig): OutfitConfig {
  if (!outfit.dress) return outfit;
  return { ...outfit, top: null, bottom: null };
}

/** Deep copy. A Look is plain JSON (zod-validated), so a JSON round-trip is exact and needs no Node/DOM global. */
export function cloneLook(look: Look): Look {
  return JSON.parse(JSON.stringify(look)) as Look;
}

// ─────────────────────────────────────────────────────────────────────────────
// describeLook — compact prose for judge prompts
// ─────────────────────────────────────────────────────────────────────────────

function heightWord(h: number): string {
  if (h < 0.95) return 'petite';
  if (h > 1.04) return 'tall';
  return 'average height';
}

function pieceLabel(slot: CatalogSlot, id: string): string {
  return catalogLabel(slot, id).toLowerCase();
}

/** Join words, dropping a word that merely repeats the one before it ("denim" + "denim blue" → "denim blue"). */
function joinDedupe(parts: string[]): string {
  const words = parts.flatMap((p) => p.split(/\s+/)).filter(Boolean);
  return words.filter((w, i) => i === 0 || w !== words[i - 1]).join(' ');
}

function describePiece(slot: CatalogSlot, piece: { id: string; color: string; pattern?: PatternId; accent?: string }): string {
  const adj = piece.pattern ? PATTERN_ADJECTIVE[piece.pattern] : '';
  const words = joinDedupe([adj, colorName(piece.color).toLowerCase(), pieceLabel(slot, piece.id)]);
  return piece.accent ? `${words} with ${colorName(piece.accent).toLowerCase()} accents` : words;
}

/**
 * Compact, human-readable description of a Look. Colours are named via the
 * nearest palette entry so the text is model-friendly and deterministic.
 */
export function describeLook(look: Look): string {
  const a = look.avatar;
  const o = normalizeOutfit(look.outfit);
  const lines: string[] = [];

  lines.push(
    `Body: ${BODY_TYPE_INFO[a.body].blurb.toLowerCase()}, ${heightWord(a.height)}; ${colorName(a.skin, SKIN_TONES).toLowerCase()} skin.`,
  );
  lines.push(
    `Hair: ${pieceLabel('hair', a.hair.style)} in ${colorName(a.hair.color, HAIR_COLORS).toLowerCase()}. ` +
      `Face: ${a.eyes.shape} ${colorName(a.eyes.color, EYE_COLORS).toLowerCase()} eyes, ${a.brows} brows, ${a.mouth === 'neutral' ? 'neutral expression' : `${a.mouth}`}.`,
  );

  const makeup: string[] = [];
  if (a.makeup.lips) makeup.push(`${colorName(a.makeup.lips, LIP_COLORS).toLowerCase()} lips`);
  if (a.makeup.blush) makeup.push(`${colorName(a.makeup.blush, BLUSH_COLORS).toLowerCase()} blush`);
  if (a.makeup.eyeshadow) makeup.push(`${colorName(a.makeup.eyeshadow, EYESHADOW_COLORS).toLowerCase()} eyeshadow`);
  if (a.makeup.liner) makeup.push('eyeliner');
  if (a.makeup.freckles) makeup.push('freckles');
  lines.push(`Makeup: ${makeup.length ? makeup.join(', ') : 'none'}.`);

  if (o.dress) {
    lines.push(`Outfit: ${describePiece('dress', o.dress)}.`);
  } else {
    const top = o.top ? describePiece('top', o.top) : 'no top';
    const bottom = o.bottom ? describePiece('bottom', o.bottom) : 'no bottom';
    lines.push(`Outfit: ${top}; ${bottom}.`);
  }
  lines.push(`Shoes: ${o.shoes ? describePiece('shoes', o.shoes) : 'barefoot'}.`);
  lines.push(
    `Accessories: ${o.accessories.length ? o.accessories.map((acc) => describePiece('accessory', acc)).join(', ') : 'none'}.`,
  );
  return lines.join('\n');
}
